import { useCallback } from 'react';
import { message } from 'antd';
import { axiosApi } from '../../../axios-api';
import { roundPositionAndSize } from '../services/PropertiesService';
import { handleGenerationsLimitError, removeDuplicates } from '../../../helpers';
import { prepareStageJson, ensureDefaultFonts } from '../services/DesignService';
import useProductStore from '../stores/useProductStore';
import { abortProductSync } from '../services/productSyncController';

export function useDesignSave({
  stage,
  designName,
  designId,
  projectId,
  canvasSize,
  userImages,
  fontsLibrary,
  initProject,
  history,
}) {
  const findCustomFontsImages = useCallback((type, library) => {
    if (!stage?.find) return [];
    const itemsList = stage.find(type);
    const itemType = type === 'Image' ? 'image' : 'text';
    const itemsUsedInCanvas = itemsList
      .filter((item) => (itemType === 'image' ? !item.attrs.dynamicField : true))
      .map((item) =>
        itemType === 'image' ? item.attrs.placeholder : item.attrs.fontUrl,
      );

    const filteredItemsList = library.filter((item) =>
      itemsUsedInCanvas.some(
        (canvasItem) =>
          canvasItem === (itemType === 'image' ? item.image : item.font) &&
          item.isCustom,
      ),
    );

    return removeDuplicates(filteredItemsList).map((item) => item.id);
  }, [stage]);

  const attachFilesToDesign = useCallback(async (id) => {
    try {
      const files = {
        images: findCustomFontsImages('Image', userImages),
        fonts: findCustomFontsImages('Text', fontsLibrary),
      };
      await axiosApi.patch(`v2/projects/designs/${id}/`, files);
    } catch (error) {
      if (error.isAxiosError) message.warning(error.message);
      else throw new Error(error);
    }
  }, [findCustomFontsImages, userImages, fontsLibrary]);

  const saveDesign = useCallback(async () => {
    try {
      if (!designName) {
        message.error('Enter graphic name!');
        return;
      }
      if (!stage) {
        message.error('Stage is not ready');
        return;
      }
      useProductStore.getState().setIsSaving(true);
      abortProductSync();

      ensureDefaultFonts(stage);
      roundPositionAndSize(stage);

      const stageObj = prepareStageJson(stage);

      const url = designId
        ? `/projects/${projectId}/designs/${designId}/`
        : `/projects/${projectId}/designs/`;
      const method = designId ? 'PUT' : 'POST';

      const { data } = await axiosApi({
        method,
        url,
        data: {
          name: designName,
          canvas_width: canvasSize.width,
          canvas_height: canvasSize.height,
          preset_name: stage.getAttr('preset_name'),
          design_json: JSON.stringify(stageObj),
        },
      });

      await attachFilesToDesign(data.id);
      message.success('Saved!');
      history.push({
        pathname: `/project/${projectId}/design/${data.id}`,
        search: window.location.search,
      });
      window.location.reload();
    } catch (error) {
      initProject();
      if (error.isAxiosError) handleGenerationsLimitError(error);
      else message.error(error.message);
    } finally {
      useProductStore.getState().setIsSaving(false);
    }
  }, [
    stage, designName, designId, projectId, canvasSize,
    attachFilesToDesign, initProject, history,
  ]);

  return { saveDesign, attachFilesToDesign, findCustomFontsImages };
}
